/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import { useContext } from 'react';
import useSWR from 'swr';
import { UseSWRQueryResult } from '../../common/types/swrTypes';
import { buildQueryString, getFromApi, postStringToApi, postToApi } from '../../common/utils/apiHelper';
import { BookableAsset } from '../assets/models/Asset';
import { ConfigContext } from '../configuration/ConfigContext';
import { usePropertyAssociationContext } from '../propertyAssociation/PropertyAssociationContext';
import { AvailableResource } from './models/AvailableResource';
import { AvailableEndTimesWithRestrictions, AvailableStartTimesWithRestrictions } from './models/AvailableTime';

type UseBookableAssetsResult = UseSWRQueryResult<BookableAsset[], 'assets'>;

export const useBookableAssets = (): UseBookableAssetsResult => {
    const { apiBaseUrl } = useContext(ConfigContext);
    const { currentPropertyAssociationId, currentArea } = usePropertyAssociationContext();

    const url = currentPropertyAssociationId
        ? `${apiBaseUrl}/propertyassociations/${currentPropertyAssociationId}/internalbookings/bookable${buildQueryString({ areaId: currentArea?.propertyAssociationAreaId })}`
        : null;

    const { data, error, isLoading } = useSWR<BookableAsset[]>(url);

    return {
        assets: data,
        isLoading,
        error,
    };
};

type UseBookableAssetResult = UseSWRQueryResult<BookableAsset, 'asset'>;

export const useBookableAsset = (assetId: string | undefined): UseBookableAssetResult => {
    const { apiBaseUrl } = useContext(ConfigContext);
    const { currentPropertyAssociationId } = usePropertyAssociationContext();

    const url = assetId && currentPropertyAssociationId ? `${apiBaseUrl}/propertyassociations/${currentPropertyAssociationId}/internalbookings/${assetId}` : null;

    const { data, error, isLoading } = useSWR<BookableAsset>(url);

    return {
        asset: data,
        isLoading,
        error,
    };
};

export const getAvailableStartTimes = (
    apiBaseUrl: string,
    propertyAssociationId: string,
    assetId: string,
    userId: string | undefined,
    data: any,
    onSuccess: (data: AvailableStartTimesWithRestrictions) => void,
    onFail: () => void,
): void => {
    if (userId) {
        postToApi(`${apiBaseUrl}/propertyassociations/${propertyAssociationId}/internalbookings/${assetId}/available/starttimes/foruser/${userId}`, data, onSuccess, onFail);
    } else {
        postToApi(
            `${apiBaseUrl}/propertyassociations/${propertyAssociationId}/internalbookings/${assetId}/available/starttimes`,
            data,
            (availableStartTimes) => onSuccess({ availableStartTimes, restrictionsPrevent: false }),
            onFail,
        );
    }
};

export const getAvailableEndTimes = (
    apiBaseUrl: string,
    propertyAssociationId: string,
    assetId: string,
    userId: string | undefined,
    data: string,
    onSuccess: (data: AvailableEndTimesWithRestrictions) => void,
    onFail: () => void,
): void => {
    if (userId) {
        postStringToApi(`${apiBaseUrl}/propertyassociations/${propertyAssociationId}/internalbookings/${assetId}/available/endtimes/foruser/${userId}`, data, onSuccess, onFail);
    } else {
        postStringToApi(
            `${apiBaseUrl}/propertyassociations/${propertyAssociationId}/internalbookings/${assetId}/available/endtimes`,
            data,
            (availableEndTimes) => onSuccess({ availableEndTimes, restrictionsPrevent: false }),
            onFail,
        );
    }
};

type UseAvailableResourcesResult = UseSWRQueryResult<AvailableResource[], 'resources'>;

export const useAvailableResources = (assetId: string | undefined, startTime: string | undefined, endTime: string | undefined): UseAvailableResourcesResult => {
    const { apiBaseUrl } = useContext(ConfigContext);
    const { currentPropertyAssociationId } = usePropertyAssociationContext();

    const key =
        assetId && startTime && endTime && currentPropertyAssociationId
            ? [`${apiBaseUrl}/propertyassociations/${currentPropertyAssociationId}/internalbookings/${assetId}/available/resources`, startTime, endTime]
            : null;

    const { data, error, isLoading } = useSWR<AvailableResource[]>(key, async ([url, start, end]) => {
        return new Promise<AvailableResource[]>((resolve, reject) => {
            postToApi(url, { startTime: start, endTime: end }, resolve, reject);
        });
    });

    return {
        resources: data,
        isLoading,
        error,
    };
};

export const bookInternally = (apiBaseUrl: string, propertyAssociationId: string, data: any, onSuccess: (data: any) => void, onFail: () => void): void => {
    postToApi(`${apiBaseUrl}/propertyassociations/${propertyAssociationId}/internalbookings/`, data, onSuccess, onFail);
};

export const bookIntermediate = (apiBaseUrl: string, propertyAssociationId: string, data: any, onSuccess: (data: any) => void, onFail: () => void): void => {
    postToApi(`${apiBaseUrl}/propertyassociations/${propertyAssociationId}/internalbookings/intermediate`, data, onSuccess, onFail);
};

export const checkRestrictions = (apiBaseUrl: string, propertyAssociationId: string, assetId: string, identifier: string, onSuccess: (data: any) => void, onFail: () => void): void => {
    getFromApi(`${apiBaseUrl}/propertyassociations/${propertyAssociationId}/internalbookings/${assetId}/restrictions/${identifier}`, onSuccess, onFail);
};
